(ns common.opensearch.filter-queries
  (:require
   [clojure.string :as string]
   [common.opensearch.utils :as os-utils]))

(def text-like-data-types
  #{:data.type/text
    :data.type/text-simple
    :data.type/text-url
    :data.type/email
    :data.type/email-multi
    :data.type/id
    :data.type/text-multi
    :data.type/url-multi
    :data.type/currency-symbol
    :data.type/keyword
    :data.type/keyword-multi})

(def keyword-or-ref-data-types
  #{:data.type/keyword
    :data.type/keyword-multi
    :data.type/ref-single
    :data.type/ref-multi})

(defn- keyword-term-capable-data-type?
  [data-type]
  (or (contains? keyword-or-ref-data-types data-type)
      (contains? text-like-data-types data-type)))

(defn normalize-field-path
  [field-path]
  (cond
    (vector? field-path) field-path
    (keyword? field-path) [field-path]
    (sequential? field-path) (vec field-path)
    :else nil))

(defn field-data-type-by-path
  [context]
  (->> (or (:accessible-fields context) [])
       (keep (fn [{:keys [field-path] :as field}]
               (when-let [normalized (normalize-field-path field-path)]
                 [normalized (or (:field/data-type field)
                                 (:data-type field))])))
       (into {})))

(defn parse-number
  [value]
  (cond
    (number? value) value
    (string? value)
    #?(:clj (try
              (Double/parseDouble value)
              (catch Throwable _ nil))
       :cljs (let [n (js/Number value)]
               (when-not (js/isNaN n) n)))
    :else nil))

(defn terms-field
  [flat-field data-type]
  (if (contains? text-like-data-types data-type)
    (str flat-field ".keyword")
    flat-field))

(defn term-field-for-value
  [flat-field data-type value]
  (let [string-like-value? (or (string? value)
                               (keyword? value)
                               (symbol? value))
        wants-keyword-field? (and string-like-value?
                                  (keyword-term-capable-data-type? data-type))]
    #?(:cljs
       (js/console.log "[os-filter-queries] term-field-for-value"
                       #js {:flatField flat-field
                            :dataType (str data-type)
                            :valueType (cond
                                         (string? value) "string"
                                         (keyword? value) "keyword"
                                         (symbol? value) "symbol"
                                         (number? value) "number"
                                         (boolean? value) "boolean"
                                         (nil? value) "nil"
                                         :else (str (type value)))
                            :valuePreview (pr-str value)
                            :stringLikeValue string-like-value?
                            :keywordTermCapable (keyword-term-capable-data-type? data-type)
                            :usesKeywordField wants-keyword-field?}))
    (if wants-keyword-field?
      (str flat-field ".keyword")
      flat-field)))

(defn contains-query-clause
  [flat-field data-type q]
  (let [q (string/trim (or q ""))]
    (when (seq q)
      (let [keyword-field (terms-field flat-field data-type)]
        {:bool
         {:should [{:wildcard {keyword-field {:value (str "*" q "*")
                                              :case_insensitive true}}}
                   {:match_phrase {flat-field q}}]
          :minimum_should_match 1}}))))

(defn blank-string-clause
  [flat-field data-type]
  (when (keyword-term-capable-data-type? data-type)
    {:term {(term-field-for-value flat-field data-type "") ""}}))

(defn value->candidate-term-values
  [value]
  (let [candidates (cond
                     (keyword? value) [(str value) (name value)]
                     (symbol? value) [(str value) (name value)]
                     :else [value])]
    (->> candidates
         (remove nil?)
         (remove (fn [v]
                   (and (string? v)
                        (string/blank? v))))
         distinct
         vec)))

(defn value->term-clause
  [field value]
  (let [values (value->candidate-term-values value)
        terms (->> values
                   (map (fn [v]
                          {:term {field v}}))
                   vec)]
    (cond
      (empty? terms) nil
      (= 1 (count terms)) (first terms)
      :else {:bool {:should terms
                    :minimum_should_match 1}})))

(defn selection->clause
  [field-path data-type selection]
  (let [flat-field (os-utils/field-path->flat-alias field-path)
        operator (:operator selection)
        value (:value selection)
        blank-clause (blank-string-clause flat-field data-type)]
    #?(:cljs
       (js/console.log "[os-filter-queries] selection->clause"
                       #js {:fieldPath (pr-str field-path)
                            :dataType (str data-type)
                            :flatField flat-field
                            :operator (str operator)
                            :valuePreview (pr-str value)}))
    (case operator
      :operand/known
      (if blank-clause
        {:bool {:must [{:exists {:field flat-field}}]
                :must_not [blank-clause]}}
        {:exists {:field flat-field}})

      :operand/unknown
      (if blank-clause
        {:bool {:should [{:bool {:must_not [{:exists {:field flat-field}}]}}
                         blank-clause]
                :minimum_should_match 1}}
        {:bool {:must_not [{:exists {:field flat-field}}]}})

      :operand/true
      {:term {flat-field true}}

      :operand/false
      {:term {flat-field false}}

      :operand/any-of
      (value->term-clause (term-field-for-value flat-field data-type value) value)

      :operand/none-of
      (when-let [term-clause (value->term-clause (term-field-for-value flat-field data-type value) value)]
        {:bool {:must_not [term-clause]}})

      :operand/contains
      (contains-query-clause flat-field data-type value)

      :operand/equals
      (value->term-clause (term-field-for-value flat-field data-type value) value)

      :operand/smaller-than
      (when-let [n (parse-number value)]
        {:range {flat-field {:lt n}}})

      :operand/greater-than
      (when-let [n (parse-number value)]
        {:range {flat-field {:gt n}}})

      :operand/between-inclusive
      (let [from (or (parse-number (:from selection)) (:from selection))
            to (or (parse-number (:to selection)) (:to selection))]
        (when (and (some? from) (some? to))
          {:range {flat-field {:gte from :lte to}}}))

      :operand/on-or-before
      (when-let [v (or (:value selection) (:to selection))]
        {:range {flat-field {:lte v}}})

      :operand/on-or-after
      (when-let [v (or (:value selection) (:from selection))]
        {:range {flat-field {:gte v}}})

      :operand/equals-date
      (when-let [v (:value selection)]
        {:term {flat-field v}})

      :operand/between-inclusive-date
      (let [from (:from selection)
            to (:to selection)]
        (when (and (some? from) (some? to))
          {:range {flat-field {:gte from :lte to}}}))

      nil)))

(defn field-filter->clause
  [field-path data-type field-filter]
  (let [clauses (->> (or (:selections field-filter) [])
                     (map (fn [selection]
                            (selection->clause field-path data-type selection)))
                     (remove nil?)
                     vec)]
    (cond
      (empty? clauses) nil
      (= 1 (count clauses)) (first clauses)
      :else {:bool {:should clauses
                    :minimum_should_match 1}})))

(defn opensearch-queries-for-facets
  [{:keys [field-path filters-by-field-path search-text context field-data-types-by-path]}]
  (let [target-path (normalize-field-path field-path)
        context-data-types-by-path (field-data-type-by-path context)
        data-types-by-path (merge context-data-types-by-path
                                  (or field-data-types-by-path {}))
        selected-filter-clauses (->> (or filters-by-field-path {})
                                     (remove (fn [[other-path _]]
                                               (= (normalize-field-path other-path) target-path)))
                                     (keep (fn [[other-path field-filter]]
                                             (let [normalized-path (normalize-field-path other-path)
                                                   data-type (get data-types-by-path normalized-path)]
                                               (when normalized-path
                                                 (field-filter->clause normalized-path data-type field-filter)))))
                                     vec)
        target-data-type (get data-types-by-path target-path)
        query-clause (when (seq (string/trim (or search-text "")))
                       (contains-query-clause (os-utils/field-path->flat-alias target-path)
                                              target-data-type
                                              search-text))]
    #?(:cljs
       (js/console.log "[os-filter-queries] opensearch-queries-for-facets"
                       #js {:targetPath (pr-str target-path)
                            :targetDataType (str target-data-type)
                            :searchText search-text
                            :filtersByFieldPathCount (count (or filters-by-field-path {}))
                            :selectedFilterClausesCount (count selected-filter-clauses)
                            :hasQueryClause (boolean query-clause)
                            :allKnownDataTypes (pr-str data-types-by-path)}))
    (vec (concat selected-filter-clauses
                 (when query-clause [query-clause])))))
